import { z } from 'zod';

/** Sent with every change request. Browsers can't add it to a cross-site form post. */
export const CSRF_HEADER = 'x-requested-with';
export const CSRF_VALUE = 'personal-os';

export const authStateSchema = z.object({
  /** The one address the app must be opened from (passkeys are tied to it). */
  appOrigin: z.string(),
  ownerExists: z.boolean(),
  signedIn: z.boolean(),
  /** True only until the owner has been created. */
  setupOpen: z.boolean(),
});
export type AuthState = z.infer<typeof authStateSchema>;

export const signInMethodSchema = z.enum(['passkey', 'authenticator', 'recovery']);
export type SignInMethod = z.infer<typeof signInMethodSchema>;

export const passkeyItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  deviceType: z.string(),
  backedUp: z.boolean(),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
});
export type PasskeyItem = z.infer<typeof passkeyItemSchema>;

export const accountSummarySchema = z.object({
  passkeys: z.array(passkeyItemSchema),
  authenticator: z.object({ enabled: z.boolean(), pending: z.boolean() }),
  recoveryCodes: z.object({
    remaining: z.number().int(),
    generatedAt: z.iso.datetime().nullable(),
  }),
  /** True if a sensitive change would go through right now without a re-check. */
  recentlyAuthenticated: z.boolean(),
});
export type AccountSummary = z.infer<typeof accountSummarySchema>;

export const sessionItemSchema = z.object({
  id: z.string(),
  deviceLabel: z.string(),
  method: signInMethodSchema,
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  network: z.string().nullable(),
  current: z.boolean(),
});
export type SessionItem = z.infer<typeof sessionItemSchema>;
export const sessionListSchema = z.object({ sessions: z.array(sessionItemSchema) });

/** Errors the screens know how to explain. */
export const AUTH_ERROR_CODES = [
  'unauthorized',
  'step_up_required',
  'invalid_token',
  'invalid_code',
  'sign_in_failed',
  'locked',
  'not_found',
  'already_set_up',
  'challenge_invalid',
  'registration_failed',
  'last_passkey',
  'already_enabled',
  'no_owner',
  'csrf',
  'invalid_request',
  'too_many_requests',
] as const;
export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

// ---- Request bodies ----

export const registrationResponseSchema = z.looseObject({
  id: z.string().min(1).max(1024),
  rawId: z.string().min(1).max(1024),
  type: z.literal('public-key'),
  response: z.looseObject({
    clientDataJSON: z.string().min(1).max(8192),
    attestationObject: z.string().min(1).max(16384),
  }),
  clientExtensionResults: z.looseObject({}),
});

export const authenticationResponseSchema = z.looseObject({
  id: z.string().min(1).max(1024),
  rawId: z.string().min(1).max(1024),
  type: z.literal('public-key'),
  response: z.looseObject({
    clientDataJSON: z.string().min(1).max(8192),
    authenticatorData: z.string().min(1).max(8192),
    signature: z.string().min(1).max(4096),
  }),
  clientExtensionResults: z.looseObject({}),
});

export const setupOptionsBodySchema = z.object({ setupToken: z.string().min(1).max(256) });
export const setupVerifyBodySchema = z.object({
  setupToken: z.string().min(1).max(256),
  challengeId: z.string().uuid(),
  response: registrationResponseSchema,
});
export const loginVerifyBodySchema = z.object({
  challengeId: z.string().uuid(),
  response: authenticationResponseSchema,
});
export const codeBodySchema = z.object({ code: z.string().min(1).max(64) });
export const passkeyAddBodySchema = z.object({
  challengeId: z.string().uuid(),
  response: registrationResponseSchema,
  label: z.string().trim().min(1).max(40).optional(),
});
export const passkeyRenameBodySchema = z.object({ label: z.string().trim().min(1).max(40) });
